import classNames from 'classnames'
import { useTranslation } from 'next-i18next'
import React, { useEffect, useCallback, useRef, useMemo } from 'react'

import { debounce } from '@src/quince-site-common/src/utils/debounce'

import { Icon } from '../icon'

import styles from './drawer.module.scss'
import CloseIcon from './icon/cross.inline.svg'

interface DrawerProps {
  placement?: 'top' | 'right' | 'bottom' | 'left'
  open?: boolean
  /** Callback when the drawer is closed */
  onClose?: () => void
  /** Whether to show the mask */
  mask?: boolean
  /** Whether to close the drawer when the mask is clicked */
  maskClosable?: boolean
  /** Whether to show the close button */
  closable?: boolean
  title?: React.ReactNode
  footer?: React.ReactNode
  children: React.ReactNode
  width?: number | string
  height?: number | string
  zIndex?: number
  /** Whether to close the drawer when ESC key is pressed */
  keyboard?: boolean
  /** Callback when the drawer open state changes */
  afterOpenChange?: (open: boolean) => void
  /** Whether to destroy the drawer content when closed */
  destroyOnClose?: boolean
  /** Additional class name for the drawer */
  className?: string
  headerClassName?: string
  bodyClassName?: string
  footerClassName?: string
  closeIconClassName?: string
  /** Unique ID for the drawer to handle scroll restoration */
  id?: string
}

export const Drawer: React.FC<DrawerProps> = ({
  placement = 'left',
  open = false,
  onClose,
  mask = true,
  maskClosable = true,
  closable = true,
  title,
  footer,
  children,
  width,
  height,
  zIndex = 1000,
  keyboard = true,
  afterOpenChange,
  destroyOnClose = false,
  className,
  headerClassName,
  bodyClassName,
  footerClassName,
  closeIconClassName,
  id = 'drawer-scroll-position',
}) => {
  const PLACEMENTS = ['top', 'right', 'bottom', 'left'] as const
  const ESCAPE_KEY = 'Escape'
  const SCROLL_DEBOUNCE_TIME = 150
  const drawerBodyRef = useRef<HTMLDivElement>(null)
  const scrollPositionKey = `drawer-scroll-position-${id}`
  const { t } = useTranslation('common')
  const observerRef = useRef<ResizeObserver | null>(null)

  const saveScrollPosition = useCallback(() => {
    if (!drawerBodyRef.current) return
    const scrollTop = drawerBodyRef.current.scrollTop
    try {
      sessionStorage.setItem(scrollPositionKey, scrollTop.toString())
    } catch (error) {
      console.error('Failed to save scroll position:', error)
    }
  }, [scrollPositionKey])

  const debouncedSaveScrollPosition = useMemo(
    () => debounce(saveScrollPosition, SCROLL_DEBOUNCE_TIME),
    [saveScrollPosition],
  )

  useEffect(() => {
    // If the drawer is not open, do nothing.
    if (!open) {
      return
    }
    // 1. Lock Body Scroll
    const originalBodyOverflow = document.body.style.overflow
    // eslint-disable-next-line i18next/no-literal-string
    document.body.style.setProperty('overflow', 'hidden', 'important')

    // 2. Setup Scroll Restoration
    const element = drawerBodyRef.current
    if (element) {
      let pendingScrollTop: number | null = null
      try {
        const savedPosition = sessionStorage.getItem(scrollPositionKey)
        if (savedPosition !== null) {
          pendingScrollTop = parseInt(savedPosition, 10)
        }
      } catch (error) {
        console.error('Failed to restore scroll position:', error)
      }

      const observer = new ResizeObserver(() => {
        if (pendingScrollTop !== null && element.scrollHeight > element.clientHeight) {
          element.scrollTop = pendingScrollTop
          pendingScrollTop = null // Prevent re-applying
        }
      })
      observer.observe(element)
      observerRef.current = observer
    }

    // 3. Return a Unified Cleanup Function
    return () => {
      // Restore body scroll
      // eslint-disable-next-line i18next/no-literal-string
      document.body.style.setProperty('overflow', originalBodyOverflow)
      // Cleanup observer
      if (observerRef.current) {
        observerRef.current.disconnect()
        observerRef.current = null
      }
      // Save final scroll position on close
      saveScrollPosition()
    }
  }, [open, children, scrollPositionKey, saveScrollPosition])

  const handleMaskClick = () => {
    if (maskClosable && onClose) {
      onClose?.()
    }
  }

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === ESCAPE_KEY && keyboard && onClose) {
        onClose?.()
      }
    },
    [keyboard, onClose],
  )

  useEffect(() => {
    if (open && keyboard) {
      window.addEventListener('keydown', handleKeyDown)
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [open, keyboard, handleKeyDown])

  useEffect(() => {
    if (afterOpenChange) {
      afterOpenChange(open)
    }
  }, [open, afterOpenChange])

  const overlayStyle = { zIndex }
  const drawerStyle = {
    zIndex: zIndex + 1,
    ...(width && (PLACEMENTS.includes(placement) ? { width } : {})),
    ...(height && (PLACEMENTS.includes(placement) ? { height } : {})),
  }

  return (
    <>
      {mask && (
        <div
          className={classNames(styles.drawerOverlay, open ? styles.open : '')}
          style={overlayStyle}
          onClick={handleMaskClick}
        />
      )}
      <div
        className={classNames(styles.drawer, styles[placement], open ? styles.open : '', className)}
        style={drawerStyle}
      >
        {title && (
          <div className={classNames(styles.drawerHeader, headerClassName)}>
            {title}
            {closable && (
              <button
                className={styles.drawerClose}
                onClick={() => {
                  onClose?.()
                }}
                aria-label={t('close_drawer')}
                type="button"
              >
                <Icon iconSvg={CloseIcon} className={classNames(styles.drawerCloseIcon, closeIconClassName)} />
              </button>
            )}
            <hr />
          </div>
        )}
        <div
          ref={drawerBodyRef}
          className={classNames(styles.drawerBody, bodyClassName)}
          onScroll={() => {
            if (open) {
              debouncedSaveScrollPosition()
            }
          }}
        >
          {open || !destroyOnClose ? children : null}
        </div>
        {footer && <div className={classNames(styles.drawerFooter, footerClassName)}>{footer}</div>}
      </div>
    </>
  )
}
